import { type FC, memo, useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  type BoxProps,
  MailIcon,
  Skeleton,
  SkeletonBox,
} from "@hightouch/htui";
import {
  useIframeDocumentReady,
  type OnIframeDocumentReady,
} from "src/components/use-iframe-document-ready";

// Width (px) every email is laid out at before being zoomed down to fit its
// container. Email mobile breakpoints commonly fire at
// `@media (max-width: 640px)`, which is inclusive — laying out at exactly 640
// would trip them into the mobile layout. 641 keeps previews just above that
// band so they render the desktop layout. Emails narrower than this (the usual
// ~600px content) center with a small gutter; emails designed wider may still
// collapse and would need a per-email measured width.
export const EMAIL_PREVIEW_DESKTOP_WIDTH = 641;

// When scrollable is true the iframe needs pointerEvents="auto" for native
// scroll, which means clicks are swallowed by the iframe's browsing context
// and never bubble to the parent. The onClick prop bridges that gap by
// listening on the iframe's contentDocument and forwarding the event.
// It is only accepted when scrollable is true because non-scrollable iframes
// use pointerEvents="none" and clicks pass through to the parent naturally.
type EmailPreviewProps = {
  html: string | null | undefined;
  width: number;
  height: number;
  isLoading?: boolean;
  onDocumentReady?: OnIframeDocumentReady;
} & (
  | { scrollable: true; onClick?: (e: React.MouseEvent) => void }
  | { scrollable?: false; onClick?: never }
) &
  Pick<BoxProps, "borderTopRadius">;

export const EmailPreview = memo<EmailPreviewProps>(function EmailPreview({
  html,
  width,
  height,
  isLoading = false,
  scrollable = false,
  onClick,
  onDocumentReady,
  ...props
}) {
  const handleDocumentLoad = useIframeDocumentReady(
    html,
    isLoading || !html,
    onDocumentReady,
  );
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;

  const handleMouseLeave = useCallback(() => {
    iframeRef.current?.contentWindow?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Scrollable iframes need pointerEvents="auto" for native scroll, which
  // means clicks are swallowed by the iframe's browsing context and never
  // reach the parent. We bridge them by listening on contentDocument and
  // forwarding to the onClick prop. A ref avoids onClick in the dep array.
  useEffect(() => {
    if (!scrollable || isLoading || !html) return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleClick = (e: MouseEvent) => {
      onClickRef.current?.(e as unknown as React.MouseEvent);
    };
    let boundDocument: Document | null = null;
    const onLoad = () => {
      boundDocument?.removeEventListener("click", handleClick);
      boundDocument = iframe.contentDocument;
      boundDocument?.addEventListener("click", handleClick);
    };

    iframe.addEventListener("load", onLoad);
    if (iframe.contentDocument?.readyState === "complete") onLoad();
    return () => {
      iframe.removeEventListener("load", onLoad);
      boundDocument?.removeEventListener("click", handleClick);
    };
  }, [scrollable, isLoading, html]);

  if (isLoading) {
    return (
      <Skeleton isLoading>
        <SkeletonBox width={width} height={height} borderRadius="none" />
      </Skeleton>
    );
  }

  if (!html) {
    return (
      <Box
        width={`${width}px`}
        height={`${height}px`}
        bg="gray.100"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box as={MailIcon} boxSize={5} color="text.tertiary" />
      </Box>
    );
  }

  // Injected styles for scrollable iframes:
  // - Hide scrollbars while keeping scroll functional
  // - cursor:pointer to match the parent card's clickable appearance
  // - pointer-events:none on all children to prevent email content hover
  //   effects (buttons, links, etc.) while still allowing scroll on body
  // - user-select:none to prevent text selection while scrolling
  const srcDoc = scrollable
    ? `<style>html{scrollbar-width:none${onClick ? ";cursor:pointer" : ""}}html::-webkit-scrollbar{display:none}body *{pointer-events:none!important}*{user-select:none!important}</style>${html}`
    : `<style>html,body{background-color:white!important;background-image:none!important}</style>${html}`;

  return (
    <Box
      width={`${width}px`}
      height={`${height}px`}
      overflow="hidden"
      position="relative"
      {...props}
      onMouseLeave={scrollable ? handleMouseLeave : undefined}
    >
      {/* Render the iframe at EMAIL_PREVIEW_DESKTOP_WIDTH and use CSS zoom
          to scale it down to the card width. We use zoom instead of
          transform: scale() because scale creates a separate GPU compositing
          layer — scrolling inside that layer causes the browser to
          re-composite neighboring elements, producing visible flicker on
          sibling cards. Zoom scales at the layout level without a separate
          compositor layer, avoiding the issue. */}
      <Box
        ref={iframeRef}
        as="iframe"
        title="Email preview"
        srcDoc={srcDoc}
        onLoad={handleDocumentLoad}
        sandbox="allow-same-origin"
        scrolling={scrollable ? "auto" : "no"}
        tabIndex={-1}
        position="absolute"
        width={`${EMAIL_PREVIEW_DESKTOP_WIDTH}px`}
        height={`${height / (width / EMAIL_PREVIEW_DESKTOP_WIDTH)}px`}
        border="none"
        pointerEvents={scrollable ? "auto" : "none"}
        top={0}
        left={0}
        sx={{ zoom: width / EMAIL_PREVIEW_DESKTOP_WIDTH }}
      />
    </Box>
  );
});

/**
 * Defers mounting the EmailPreview iframe until the placeholder scrolls near
 * the viewport. Keeps the card layout identical — just swaps a lightweight
 * div for the heavy iframe until it's needed.
 */
export const LazyEmailPreview: FC<EmailPreviewProps> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!visible) {
    return (
      <Box
        ref={ref}
        width={`${props.width}px`}
        height={`${props.height}px`}
        bg="base.lightBackground"
      />
    );
  }

  return <EmailPreview {...props} />;
};
